import { compassKey } from './projectVersionChanges.action'

export interface DegreesMinutes {
  degrees: string
  minutes: string
}

const MINUTE_PRECISION = 1000

export const toDegreesMinutes = (value: number): DegreesMinutes => {
  const sign = value < 0 ? -1 : 1
  let degrees = Math.floor(Math.abs(value))
  let minutes = Math.round((Math.abs(value) - degrees) * 60 * MINUTE_PRECISION) / MINUTE_PRECISION
  if (minutes >= 60) {
    degrees += 1
    minutes = 0
  }
  return { degrees: String(sign * degrees), minutes: minutes.toFixed(3) }
}

// Undefined when the text is not a valid coordinate.
export const fromDegreesMinutes = (input: DegreesMinutes, maxDegrees: number): number | undefined => {
  const degrees = Number(input.degrees)
  const minutes = Number(input.minutes)
  if (input.degrees.trim() === '' || input.minutes.trim() === '') return undefined
  if (!Number.isInteger(degrees) || !Number.isFinite(minutes) || minutes < 0 || minutes >= 60) return undefined
  if (Math.abs(degrees) > maxDegrees) return undefined

  const sign = input.degrees.trim().startsWith('-') ? -1 : 1
  return Number((sign * (Math.abs(degrees) + minutes / 60)).toFixed(8))
}

const METERS_PER_DEGREE = 111320

// Distance and compass bearing between two coordinates, as the revision changes report moves.
export const measureMove = (
  from: { latitude: number; longitude: number },
  to: { latitude: number; longitude: number },
) => {
  const north = (to.latitude - from.latitude) * METERS_PER_DEGREE
  const east = (to.longitude - from.longitude) * METERS_PER_DEGREE * Math.cos((from.latitude * Math.PI) / 180)
  return {
    meters: Math.sqrt(north * north + east * east),
    bearing: ((Math.atan2(east, north) * 180) / Math.PI + 360) % 360,
  }
}

type Point = { latitude: number; longitude: number }
type TranslateFn = (key: string, options?: Record<string, string | number>) => string

const LARGE_MOVE_METERS = 80

export interface CoordinateMoveContext {
  current: Point
  // The same position in the published version; missing for a position the draft added.
  published?: Point
  publishedLabel: string
  // The corner a mooring line starts from, so its length can be given.
  lineStart?: Point
  isAnchor: boolean
}

// The text under the coordinate fields: how far the new coordinates move the position, compared with now and with the published version.
export const describeCoordinateMove = (next: Point | undefined, context: CoordinateMoveContext, t: TranslateFn) => {
  if (!next) return { text: t('revision-coordinates-invalid'), tone: 'error' as const }
  const { current, published, publishedLabel, lineStart, isAnchor } = context
  const direction = (bearing: number) => t(compassKey(bearing))
  const fromNow = measureMove(current, next)
  const lengthText = (point: Point, key: string) =>
    lineStart ? t(key, { meters: Math.round(measureMove(lineStart, point).meters) }) : ''

  if (fromNow.meters < 0.5) {
    const intro = t(isAnchor ? 'revision-anchor-hint' : 'revision-coordinates-hint')
    return {
      text: [intro, lengthText(current, 'revision-line-length-now')].filter(Boolean).join(' '),
      tone: 'normal' as const,
    }
  }

  const fromPublished = published ? measureMove(published, next) : undefined
  const parts = [
    t('revision-move-from-current', {
      subject: t(isAnchor ? 'revision-subject-anchor' : 'revision-subject-position'),
      meters: Math.round(fromNow.meters),
      direction: direction(fromNow.bearing),
    }),
    fromPublished && fromPublished.meters >= 1
      ? t('revision-move-from-published', {
          meters: Math.round(fromPublished.meters),
          direction: direction(fromPublished.bearing),
          version: publishedLabel,
        })
      : '',
    lengthText(next, 'revision-line-length-becomes'),
    fromNow.meters > LARGE_MOVE_METERS ? t('revision-move-large') : '',
  ]
  return {
    text: parts.filter(Boolean).join(' '),
    tone: fromNow.meters > LARGE_MOVE_METERS ? ('warning' as const) : ('normal' as const),
  }
}

// "69° 40.063′ N · 18° 11.079′ E"; undefined when the position has no coordinates.
export const formatPosition = (point?: { latitude: number; longitude: number }) => {
  if (!point) return undefined
  const part = (value: number, positive: string, negative: string) => {
    const { degrees, minutes } = toDegreesMinutes(Math.abs(value))
    return `${degrees}\u00b0 ${minutes}\u2032 ${value < 0 ? negative : positive}`
  }
  return `${part(point.latitude, 'N', 'S')} \u00b7 ${part(point.longitude, 'E', 'W')}`
}
